/**
 * 共通HTTPクライアントユーティリティ
 *
 * 各サービスで共通で使用する機能を提供:
 * - compact(): 空値を除去したオブジェクト作成
 * - handleAxiosError(): エラーハンドリング
 * - postWithTrace(): trace関連ヘッダー付きPOSTリクエスト
 */
import axios, { AxiosError } from "axios";
import { get_user_facing_http_error_message } from "../utils/errorMessage";

/**
 * トレースコンテキスト（W3C Trace Context）
 */
export interface TraceContext {
  traceId?: string;
  traceparent?: string;
}

/**
 * 空値（undefined, null, ""）を除去したオブジェクトを作成
 */
export function compact(obj: Record<string, any>): Record<string, any> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);
}

/**
 * エラー詳細をフォーマット
 * プロキシ経由のレスポンスでは data.api_data に上流APIのボディが入るため、そこからも抽出する
 */
export function formatErrorDetails(data: any): string | null {
  if (!data) {
    return null;
  }

  if (typeof data === "string") {
    return data;
  }

  const messages: string[] = [];

  // FastAPI 直返却: { "detail": string } または検証エラー配列
  const pushFastApiDetail = (detail: unknown): void => {
    if (detail === undefined || detail === null) {
      return;
    }
    if (typeof detail === "string") {
      const s = detail.trim();
      if (s) messages.push(s);
      return;
    }
    if (Array.isArray(detail)) {
      const parts: string[] = [];
      for (const entry of detail) {
        if (typeof entry === "string") {
          const t = entry.trim();
          if (t) parts.push(t);
        } else if (entry && typeof entry === "object" && "msg" in entry) {
          const msg = (entry as { msg?: unknown }).msg;
          if (typeof msg === "string" && msg.trim()) {
            parts.push(msg.trim());
          }
        }
      }
      if (parts.length > 0) {
        messages.push(parts.join("; "));
      }
    }
  };

  pushFastApiDetail(data.detail);

  // プロキシ経由のAPIレスポンスからdetailメッセージを優先的に取得
  if (data.api_data?.detail && typeof data.api_data.detail === "string") {
    messages.push(data.api_data.detail.trim());
  }

  if (typeof data.message === "string" && data.message.trim()) {
    messages.push(data.message.trim());
  }

  if (Array.isArray(data.errors)) {
    data.errors.forEach(
      (err: { domain?: string; reason?: string; message?: string }) => {
        const parts = [
          typeof err.domain === "string" ? err.domain.trim() : "",
          typeof err.reason === "string" ? err.reason.trim() : "",
          typeof err.message === "string" ? err.message.trim() : "",
        ].filter(Boolean);

        if (parts.length > 0) {
          messages.push(parts.join(" / "));
        }
      }
    );
  }

  if (typeof data.error === "string" && data.error.trim()) {
    messages.push(data.error.trim());
  }

  if (
    data.extensions &&
    typeof data.extensions.message === "string" &&
    data.extensions.message.trim()
  ) {
    messages.push(data.extensions.message.trim());
  }

  // プロキシが返す api_data オブジェクトからもメッセージを抽出（上流APIの内容）
  if (data.api_data && typeof data.api_data === "object" && messages.length === 0) {
    const fromApiData = formatErrorDetails(data.api_data);
    if (fromApiData) messages.push(fromApiData);
  }

  return messages.length > 0 ? messages.join("; ") : null;
}

/**
 * Axiosエラーをハンドリングして適切なエラーをthrow
 * UIには技術的メッセージ（スタックトレース等）を出さず、ユーザー向け文言のみ渡す
 */
export function handleAxiosError(error: any, serviceName: string = "API"): never {
  console.error(`${serviceName} error`, error);

  if (error?.response) {
    const status = error.response.status;
    const detail = formatErrorDetails(error.response.data);
    const userMessage = get_user_facing_http_error_message(
      status,
      detail,
      error.response.data
    );
    throw new Error(userMessage);
  }

  if (error?.code === "ECONNABORTED") {
    throw new Error("リクエストがタイムアウトしました");
  }

  throw new Error(`ネットワークエラー: ${error?.message || "Unknown error"}`);
}

/**
 * トレースヘッダー付きPOSTリクエスト
 *
 * @param baseUrl ベースURL
 * @param path エンドポイントパス
 * @param payload リクエストボディ
 * @param traceContext トレースコンテキスト（traceparentヘッダー用）
 * @param timeout タイムアウト（ミリ秒）
 */
export async function postWithTrace<T>(
  baseUrl: string,
  path: string,
  payload: Record<string, any>,
  traceContext?: TraceContext,
  timeout: number = 600_000
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (traceContext?.traceparent) {
    headers["traceparent"] = traceContext.traceparent;
  }

  try {
    const response = await axios.post(`${baseUrl}${path}`, payload, {
      headers,
      timeout,
    });
    return response.data as T;
  } catch (error: any) {
    handleAxiosError(error);
  }
}

/**
 * GETリクエスト
 */
export async function get<T>(
  baseUrl: string,
  path: string,
  timeout: number = 10000
): Promise<T> {
  try {
    const response = await axios.get(`${baseUrl}${path}`, {
      timeout,
    });
    return response.data as T;
  } catch (error: any) {
    handleAxiosError(error);
  }
}
